import * as React from "react";

import { FormField } from "@/components/ui/form-field";
import { StatsGrid, type StatTile } from "@/components/ui/stats-grid";
import { Textarea } from "@/components/ui/textarea";
import { formatDate } from "@/lib/format-date";
import type { ReversalPreflightData } from "@/hooks/queries/admin-users-types";
import { DangerConfirmDialog } from "@/routes/admin/DangerConfirmDialog";

// Story 22.3 (FR88) — Screen 39 Panels 2a and 2b, on the shape
// `AttachToOrganizationDialog` already uses: `DangerConfirmDialog` with
// `confirmVariant="primary"`. NOT a `btn danger` (D11): the reversal removes an
// organization seat, not an account, and dressing it as destruction
// miscommunicates what the administrator is doing.
//
// EVERYTHING THE DIALOG KNOWS ABOUT THE WINDOW COMES FROM THE PREFLIGHT (AC25).
// There is no date comparison in this file and no window length in this bundle:
// a browser that re-derived either would drift the moment somebody changed
// `SEAT_ATTACHMENT_CORRECTION_WINDOW_DAYS`, and drift towards offering a
// Confirm the server will refuse. `reversible`, `window_closes_at` and
// `window_days` are read, never computed.
//
// The seven D11 rows, in order: the two-state window banner, the four-stat seat
// impact, "what does not come back", "what is not affected", "who can reverse",
// the required Reason, and a Confirm that is disabled with its reason outside
// the window. The three sentences render in BOTH states — AC26 for the first
// two, and the PM's 2026-09-21 ruling for the third.

/** D1 — the reversal does not re-open what the attach cancelled. Both states. */
export const NO_SUBSCRIPTION_BACK_SENTENCE =
  "The cancelled individual subscription is not restored — individual access after a reversal is a new purchase.";

/** D2 — the contrast that stops this reading as 14.6's removal. Both states. */
export const ACCOUNT_UNAFFECTED_SENTENCE =
  "The account stays enabled and existing sessions keep working.";

/**
 * D11's seventh row, added on the PM's ruling of 2026-09-21. Screen 39 states
 * this only in Panel 2's caption and draws it in neither panel, so it is
 * asserted by AC25 or nowhere.
 *
 * A STATEMENT, never a gate: the server refusal is the boundary, and this
 * sentence restates AC5's capability grant for an administrator who is deciding
 * whether this dialog is even their route.
 */
export const WHO_CAN_REVERSE_SENTENCE =
  "Only a CITS super_admin can reverse an attachment. The organization's own administrator cannot — their route is the FR83 seat removal, which does not give the seat back.";

export interface ReverseAttachmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  orgName: string;
  /** `undefined` while the preflight GET is in flight or has failed. */
  preflight?: ReversalPreflightData;
  preflightLoading?: boolean;
  isPending: boolean;
  onConfirm: (values: { reason: string }) => void;
}

export function ReverseAttachmentDialog({
  open,
  onOpenChange,
  userName,
  orgName,
  preflight,
  preflightLoading = false,
  isPending,
  onConfirm,
}: ReverseAttachmentDialogProps) {
  const [reason, setReason] = React.useState("");

  // Every re-open starts blank — an administrator who cancels one reversal and
  // opens the dialog on another must not arrive carrying the first one's note
  // into a second account's audit trail.
  React.useEffect(() => {
    if (!open) return;
    setReason("");
  }, [open]);

  // UNKNOWN IS NOT OPEN. While the preflight is unresolved the dialog says so
  // and Confirm stays disabled: an unanswered eligibility read must never
  // render as "inside the window", which is the one direction this banner must
  // not fail in.
  const reversible = preflight?.reversible === true;
  const hasReason = reason.trim().length > 0;

  const seatTiles: StatTile[] = preflight
    ? [
        {
          label: "Seat capacity",
          value: preflight.seat_capacity_after,
          foot: "Unchanged",
        },
        {
          label: "Assigned",
          value: preflight.seat_consumed_after,
          foot: reversible ? `−1 — was ${preflight.seat_consumed_after + 1}` : "Unchanged",
        },
        {
          label: "Active seats",
          value: preflight.active_seat_count_after,
          foot: reversible
            ? `−1 — was ${preflight.active_seat_count_after + 1}`
            : "Unchanged",
        },
        {
          label: "Available",
          value: preflight.remaining_after,
          foot: reversible ? `+1 — was ${preflight.remaining_after - 1}` : "Unchanged",
        },
      ]
    : [];

  return (
    <DangerConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Reverse attachment"
      description={`${userName} will be taken off ${orgName}'s roster and the seat will return to its pool.`}
      mustRead={
        <WindowStatus preflight={preflight} preflightLoading={preflightLoading} />
      }
      mustReadVariant={reversible ? "info" : "warn"}
      confirmVariant="primary"
      confirmLabel="Reverse attachment"
      pendingLabel="Reversing…"
      isPending={isPending}
      // The server refusal stays the gate; this only stops a request that is
      // already known to fail, and a submit with no reason, which FR88 requires
      // and which the server would 422 without a sentence an admin can act on.
      confirmDisabled={!reversible || !hasReason}
      // A disabled button fires no events, so the reason has to be reachable by
      // pointer as well as by reading the banner — Screen 39's field contract
      // names the `title` on the closed-window state specifically. Server text
      // again: the date is the one the preflight computed, never a local one.
      confirmTitle={confirmTitle(preflight, reversible, hasReason)}
      onConfirm={() => onConfirm({ reason: reason.trim() })}
    >
      <div className="grid gap-4">
        {/* Not a FormField: there is no control here to label. */}
        <section aria-labelledby="reverse-seat-impact" className="grid gap-[6px]">
          <h3
            id="reverse-seat-impact"
            className="text-sm font-medium text-[color:var(--text-secondary)]"
          >
            Seat impact — after this reversal
          </h3>
          {preflight ? (
            <StatsGrid stats={seatTiles} />
          ) : (
            <p className="text-sm text-[color:var(--text-tertiary)]">
              Checking this organization's seats…
            </p>
          )}
        </section>

        <FormField
          label="Reason"
          htmlFor="reverse-reason"
          required
          helper="Required. Recorded on the audit trail of the reversal and of a refused reversal alike — neither is suppressible. Please do not enter patient information."
        >
          <Textarea
            id="reverse-reason"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Why is this attachment being corrected?"
          />
        </FormField>

        <div className="grid gap-2 text-sm text-[color:var(--text-tertiary)]">
          <p>
            <strong>What does not come back.</strong> {NO_SUBSCRIPTION_BACK_SENTENCE}
          </p>
          <p>
            <strong>What is not affected.</strong> {ACCOUNT_UNAFFECTED_SENTENCE}
          </p>
          <p>
            <strong>Who can reverse.</strong> {WHO_CAN_REVERSE_SENTENCE}
          </p>
        </div>
      </div>
    </DangerConfirmDialog>
  );
}

/**
 * The tooltip on a disabled Confirm. `undefined` whenever the button is live —
 * a title on an enabled control is noise, and the one case the field contract
 * asks for is the closed window.
 */
function confirmTitle(
  preflight: ReversalPreflightData | undefined,
  reversible: boolean,
  hasReason: boolean,
): string | undefined {
  if (!preflight) {
    return "The correction window has not been checked yet, so this reversal cannot be offered.";
  }
  if (!reversible) {
    return preflight.refusal_code === "ORG-031"
      ? `The correction window closed${
          preflight.window_closes_at ? ` on ${formatDate(preflight.window_closes_at)}` : ""
        } — this attachment can no longer be reversed.`
      : "This attachment cannot be reversed.";
  }
  return hasReason ? undefined : "Enter a reason to confirm this reversal.";
}

/**
 * D11's first row — the two-state banner, in the two sentences the mockup draws.
 *
 * Every branch here reads a SERVER field. The closed-window state names the date
 * the server computed and the length the server configured; the dialog does no
 * arithmetic over either.
 */
function WindowStatus({
  preflight,
  preflightLoading,
}: {
  preflight?: ReversalPreflightData;
  preflightLoading: boolean;
}) {
  if (!preflight) {
    return (
      <span>
        {preflightLoading
          ? "Checking whether this attachment is still inside its correction window…"
          : "The correction window could not be checked, so this reversal cannot be offered. Reload and try again."}
      </span>
    );
  }

  if (preflight.reversible) {
    return (
      <div className="grid gap-1">
        <span>
          Inside the correction window — the seat returns to the organization's
          pool.
        </span>
        {preflight.window_closes_at ? (
          <span>The window closes on {formatDate(preflight.window_closes_at)}.</span>
        ) : null}
      </div>
    );
  }

  // Every non-reversible answer lands here, and the sentence has to stay true
  // for all of them: `ORG-029` (no attachment) and `ORG-030` (an invited seat)
  // are not window facts, so the closed-window wording is reserved for the code
  // that actually means it.
  if (preflight.refusal_code === "ORG-031") {
    return (
      <div className="grid gap-1">
        <span>
          The correction window closed
          {preflight.window_closes_at
            ? ` on ${formatDate(preflight.window_closes_at)}`
            : ""}{" "}
          — this attachment can no longer be reversed.
        </span>
        <span>The window is {preflight.window_days} days from the attachment date.</span>
      </div>
    );
  }

  return (
    <div className="grid gap-1">
      <span>This attachment cannot be reversed.</span>
      <span>
        {preflight.refusal_code === "ORG-030"
          ? "This seat was created by the organization's own invitation, not by a CITS attachment, so there is no attachment to correct."
          : preflight.refusal_code === "ORG-027"
            ? "This account has been deleted, and a deleted account admits no administrative action."
            : "This clinician does not hold an active seat on this organization."}
      </span>
    </div>
  );
}
